import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

/** Props for AddressDetailsTextField. */
export type AddressDetailsTextFieldProps = TextFieldProps & {
  maxLength?: number;
  containerSx?: SxProps<Theme>;
  /** Show red helper text without relying on the TextField error border alone. */
  helperTextError?: boolean;
};

const fieldSx: SxProps<Theme> = {
  '.css-l2dxek-MuiInputBase-root-MuiOutlinedInput-root': {
    fontSize: '14px',
  },
  label: {
    fontSize: '14px',
  },
  '.css-wgai2y-MuiFormLabel-asterisk': {
    color: 'danger.main',
  },
};

/** Multiline address field with character counter and length validation UI. */
export function AddressDetailsTextField({
  maxLength = 500,
  containerSx,
  value = '',
  error = false,
  helperText = '',
  helperTextError,
  sx,
  InputProps,
  label = 'Address Details',
  rows = 4,
  ...textFieldProps
}: AddressDetailsTextFieldProps) {
  const charCount = String(value ?? '').length;
  const isOverLimit = charCount > maxLength;
  const helperMessage =
    helperText || (isOverLimit ? `Address details cannot exceed ${maxLength} characters` : '');
  const showHelperError = Boolean(helperTextError || error || isOverLimit);
  const showFieldError = Boolean(error || isOverLimit);

  return (
    <Box sx={{ position: 'relative', ...containerSx }}>
      <TextField
        {...textFieldProps}
        label={label}
        value={value}
        error={showFieldError}
        multiline
        variant="outlined"
        size="small"
        fullWidth
        rows={rows}
        sx={[fieldSx, sx] as SxProps<Theme>}
        InputProps={{
          ...InputProps,
          classes: {
            ...InputProps?.classes,
            input: 'custom-scrollbar',
          },
        }}
      />
      <FormHelperText
        error={showHelperError}
        sx={{
          ml: '10px',
          fontSize: '11px',
          mt: '2px',
          mb: 0,
          ...(showHelperError ? { color: 'error.main' } : {}),
        }}
      >
        {helperMessage}
      </FormHelperText>
      <Box
        sx={{
          position: 'absolute',
          top: '-7px',
          right: 10,
          zIndex: 10,
          backgroundColor: 'white',
          fontSize: '10px',
          width: '50px',
          textAlign: 'center',
        }}
      >
        <Box component="span">
          <span style={{ color: isOverLimit ? 'red' : 'black' }}>{charCount}</span>
          <span>/{maxLength}</span>
        </Box>
      </Box>
    </Box>
  );
}

export default AddressDetailsTextField;
